import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaSignOutAlt,
  FaBell,
  FaCog,
  FaUserEdit,
  FaLock,
  FaTrash,
  FaCalendarCheck,
  FaTimes,
} from "react-icons/fa";
import { useSelector, useDispatch } from "react-redux";

import { logoutUser } from "../../../redux/slices/authSlice";
import Notification from "../Notification";
import SearchBar from "../../SearchBar/SearchBar";
import { getBusinessUserProfileImageUrl } from "../../utils/buserimageUtils";
import { deleteBusinessAccount } from "../../../redux/buuserdashboard/delete";
import BusinessOnlineStatus from "../../BusinessOnlineStatus";
import { updateOnlineStatus } from "../../../redux/apis/Businessuser/onlinestatus";

const DashboardHeader = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // =====================================================
  // REDUX
  // =====================================================

  const user = useSelector((state) => state.auth.user);

  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  const notifications = useSelector((state) => state.notification?.items || []);

  // =====================================================
  // STATE
  // =====================================================

  const [showNotifications, setShowNotifications] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  // =====================================================
  // CATEGORY
  // =====================================================

  const primaryCategory = (user?.categoryPath?.[0] || "").toLowerCase();

  // =====================================================
  // MANAGE SLOTS
  // =====================================================

  const handleManageSlots = () => {
    const category = primaryCategory;

    if (category.includes("alternative")) {
      navigate("/alternative-avail-slots");
      return;
    }

    if (category.includes("doctor")) {
      if (!user?._id) {
        console.error("User ID missing");
        return;
      }

      navigate(`/doctor/slots/${user._id}`);
      return;
    }

    if (category.includes("other medical") || category.includes("medical")) {
      navigate("/availabilities/other-medical/slots");
      return;
    }

    navigate("/");
  };

  // =====================================================
  // PROFILE IMAGE
  // =====================================================

  const profileUrl = useMemo(() => {
    return getBusinessUserProfileImageUrl(user?.profilePic || null);
  }, [user?.profilePic]);

  // =====================================================
  // NOTIFICATIONS
  // =====================================================

  const unreadCount = notifications.filter(
    (notification) => !notification.read,
  ).length;

  // =====================================================
  // LOGOUT
  // =====================================================

  const handleLogout = async () => {
    try {
      // ================================================
      // BUSINESS USER → OFFLINE
      // ================================================

      if (user?.role === "business" && user?._id) {
        await dispatch(
          updateOnlineStatus({
            userId: user._id,
            isOnline: false,
            role: "business",
          }),
        ).unwrap();

        console.log("✅ Business user marked OFFLINE");
      }
    } catch (error) {
      console.error("❌ Failed to mark business user offline:", error);

      // Don't prevent logout
    }

    // ================================================
    // LOGOUT
    // ================================================

    dispatch(logoutUser());

    setShowDropdown(false);
    setShowNotifications(false);

    navigate("/login");
  };

  // =====================================================
  // DELETE ACCOUNT
  // =====================================================

  const handleDeleteAccount = () => {
    if (!window.confirm("Delete your account permanently?")) {
      return;
    }

    if (!user?._id) {
      alert("User not authenticated");
      return;
    }

    dispatch(deleteBusinessAccount(user._id))
      .unwrap()
      .then(() => {
        alert("Account deleted successfully");
        handleLogout();
      })
      .catch((err) => {
        console.error("Delete account error:", err);

        alert(
          typeof err === "string"
            ? err
            : err?.message || "Something went wrong",
        );
      });
  };

  // =====================================================
  // NOT AUTHENTICATED
  // =====================================================

  if (!isAuthenticated) {
    return (
      <header
        className="
          w-full
          bg-gradient-to-r
          from-[#0f1f33]
          via-[#1a3a5c]
          to-[#0ea5e9]
          px-4
          py-3
          text-center
          text-sm
          font-medium
          text-white
          shadow-md
        "
      >
        Please log in to see your dashboard.
      </header>
    );
  }

  // =====================================================
  // UI
  // =====================================================

  return (
    <>
      {/* =====================================================
          COMPLETE NAVIGATION / HEADER
      ===================================================== */}

      <header
        className="
          relative
          z-50
          w-full
          overflow-visible
          border-b
          border-[#0b1728]
          bg-gradient-to-r
          from-[#0b1829]
          via-[#102f4d]
          via-[#17466b]
          to-[#087ea8]
          text-white
          shadow-[0_4px_18px_rgba(15,31,51,0.25)]
        "
      >
        {/* =================================================
            TOP LIGHT / COLOR STRIP
        ================================================= */}

        <div
          className="
            absolute
            top-0
            left-0
            right-0
            h-1
            bg-gradient-to-r
            from-[#38bdf8]
            via-[#0ea5e9]
            to-[#22d3ee]
          "
        />

        {/* =================================================
            MAIN NAVIGATION CONTAINER
        ================================================= */}

        <div
          className="
            mx-auto
            w-full
            max-w-[1800px]
            px-3
            py-3
            sm:px-5
            lg:px-8
          "
        >
          <div
            className="
              flex
              flex-col
              gap-3
              lg:flex-row
              lg:items-center
              lg:justify-between
            "
          >
            {/* =================================================
                LEFT - PROFILE
            ================================================= */}

            <div
              className="
                flex
                min-w-0
                items-center
                gap-3
                rounded-2xl
                border
                border-white/10
                bg-white/[0.06]
                px-2.5
                py-2
                shadow-inner
                backdrop-blur-sm
                lg:w-[280px]
                lg:shrink-0
              "
            >
              {/* PROFILE IMAGE */}

              <div className="relative shrink-0">
                <img
                  src={profileUrl}
                  alt="User profile"
                  className="
                    h-11
                    w-11
                    rounded-full
                    border-2
                    border-cyan-200/80
                    bg-white
                    object-cover
                    shadow-lg
                    sm:h-12
                    sm:w-12
                  "
                  onError={(event) => {
                    event.currentTarget.src =
                      getBusinessUserProfileImageUrl(null);
                  }}
                />

                {/* ONLINE INDICATOR */}

                <span
                  className="
                    absolute
                    bottom-0
                    right-0
                    h-3
                    w-3
                    rounded-full
                    border-2
                    border-[#123654]
                    bg-emerald-400
                    shadow-[0_0_8px_rgba(52,211,153,0.7)]
                  "
                />
              </div>

              {/* USER DETAILS */}

              <div className="min-w-0">
                <h1
                  className="
                    truncate
                    text-sm
                    font-bold
                    text-white
                    sm:text-base
                  "
                >
                  {user?.fullName || "User"}
                </h1>

                <p
                  className="
                    truncate
                    text-[11px]
                    font-medium
                    text-cyan-100
                    sm:text-xs
                  "
                >
                  {user?.profession || "Healthcare Professional"}
                </p>
              </div>
            </div>

            {/* =================================================
                CENTER - SEARCH
            ================================================= */}

            <div className="w-full min-w-0 flex-1 lg:px-4">
              <div className="mx-auto w-full max-w-2xl">
                <div
                  className="
                    rounded-2xl
                    border
                    border-white/10
                    bg-white/[0.08]
                    p-1
                    shadow-inner
                    backdrop-blur-sm
                  "
                >
                  <SearchBar />
                </div>
              </div>
            </div>

            {/* =================================================
                ONLINE STATUS
            ================================================= */}

            <div
              className="
                flex
                items-center
                justify-center
                rounded-xl
                border
                border-emerald-300/20
                bg-emerald-400/10
                px-2
                py-1.5
                shadow-sm
                backdrop-blur-sm
              "
            >
              <BusinessOnlineStatus />
            </div>

            {/* =================================================
                RIGHT - ACTIONS
            ================================================= */}

            <div
              className="
                flex
                w-full
                min-w-0
                items-center
                justify-end
                gap-2
                lg:w-auto
                lg:shrink-0
              "
            >
              {/* =================================================
                  MANAGE SLOTS
              ================================================= */}

              {/* =================================================
                  NOTIFICATION
              ================================================= */}

              <button
                type="button"
                onClick={() => {
                  setShowNotifications(true);
                  setShowDropdown(false);
                }}
                aria-label="Notifications"
                className="
                  relative
                  flex
                  h-10
                  w-10
                  shrink-0
                  items-center
                  justify-center
                  rounded-xl
                  border
                  border-white/15
                  bg-white/[0.10]
                  text-white
                  shadow-sm
                  backdrop-blur-sm
                  transition-all
                  duration-200
                  hover:-translate-y-0.5
                  hover:bg-white/[0.20]
                  hover:shadow-md
                "
              >
                <FaBell className="text-sm" />

                {unreadCount > 0 && (
                  <span
                    className="
                      absolute
                      -right-1
                      -top-1
                      flex
                      min-h-[18px]
                      min-w-[18px]
                      items-center
                      justify-center
                      rounded-full
                      border
                      border-white/30
                      bg-red-500
                      px-1
                      text-[9px]
                      font-bold
                      text-white
                      shadow-lg
                    "
                  >
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </button>

              {/* =================================================
                  ACCOUNT
              ================================================= */}

              <div className="relative">
                <button
                  type="button"
                  onClick={() => {
                    setShowDropdown((prev) => !prev);
                    setShowNotifications(false);
                  }}
                  className="
                    flex
                    h-10
                    items-center
                    justify-center
                    gap-2
                    rounded-xl
                    border
                    border-white/15
                    bg-white/[0.10]
                    px-3
                    text-xs
                    font-semibold
                    text-white
                    shadow-sm
                    backdrop-blur-sm
                    transition-all
                    duration-200
                    hover:-translate-y-0.5
                    hover:bg-white/[0.20]
                    hover:shadow-md
                    sm:text-sm
                  "
                >
                  <FaCog
                    className={`
                      transition-transform
                      duration-300
                      ${showDropdown ? "rotate-90" : ""}
                    `}
                  />

                  <span className="hidden sm:inline">Account</span>
                </button>

                {/* =================================================
                    ACCOUNT DROPDOWN
                ================================================= */}

                {showDropdown && (
                  <>
                    {/* MOBILE / OUTSIDE BACKDROP */}

                    <div
                      className="
                        fixed
                        inset-0
                        z-40
                        bg-transparent
                      "
                      onClick={() => setShowDropdown(false)}
                    />

                    <div
                      className="
                        absolute
                        right-0
                        top-full
                        z-50
                        mt-2
                        w-60
                        overflow-hidden
                        rounded-2xl
                        border
                        border-[#d8e3ee]
                        bg-white
                        text-gray-700
                        shadow-[0_15px_40px_rgba(15,31,51,0.20)]
                      "
                    >
                      {/* DROPDOWN HEADER */}

                      <div
                        className="
                          border-b
                          border-white/10
                          bg-gradient-to-r
                          from-[#0f1f33]
                          via-[#1a3a5c]
                          to-[#0ea5e9]
                          px-4
                          py-4
                          text-white
                        "
                      >
                        <p
                          className="
                            text-[9px]
                            font-bold
                            uppercase
                            tracking-[0.16em]
                            text-cyan-200
                          "
                        >
                          Account
                        </p>

                        <p
                          className="
                            mt-1
                            truncate
                            text-sm
                            font-bold
                            text-white
                          "
                        >
                          {user?.fullName || "User"}
                        </p>

                        <p
                          className="
                            mt-0.5
                            truncate
                            text-[10px]
                            text-blue-100
                          "
                        >
                          {user?.profession || "Healthcare Professional"}
                        </p>
                      </div>

                      {/* UPDATE PROFILE */}

                      <button
                        type="button"
                        onClick={() => {
                          navigate("/business/edit-profile");
                          setShowDropdown(false);
                        }}
                        className="
                          flex
                          w-full
                          items-center
                          gap-3
                          border-b
                          border-gray-100
                          px-4
                          py-3
                          text-left
                          text-sm
                          font-medium
                          text-gray-700
                          transition
                          hover:bg-blue-50
                          hover:text-[#0ea5e9]
                        "
                      >
                        <span
                          className="
                            flex
                            h-8
                            w-8
                            items-center
                            justify-center
                            rounded-lg
                            bg-blue-50
                            text-blue-600
                          "
                        >
                          <FaUserEdit />
                        </span>

                        <span>Update Profile</span>
                      </button>

                      {/* CHANGE PASSWORD */}

                      <button
                        type="button"
                        onClick={() => {
                          navigate("/business/change-password");
                          setShowDropdown(false);
                        }}
                        className="
                          flex
                          w-full
                          items-center
                          gap-3
                          border-b
                          border-gray-100
                          px-4
                          py-3
                          text-left
                          text-sm
                          font-medium
                          text-gray-700
                          transition
                          hover:bg-blue-50
                          hover:text-[#0ea5e9]
                        "
                      >
                        <span
                          className="
                            flex
                            h-8
                            w-8
                            items-center
                            justify-center
                            rounded-lg
                            bg-cyan-50
                            text-cyan-600
                          "
                        >
                          <FaLock />
                        </span>

                        <span>Change Password</span>
                      </button>

                      {/* DELETE ACCOUNT */}

                      <button
                        type="button"
                        onClick={() => {
                          setShowDropdown(false);
                          handleDeleteAccount();
                        }}
                        className="
                          flex
                          w-full
                          items-center
                          gap-3
                          border-b
                          border-gray-100
                          px-4
                          py-3
                          text-left
                          text-sm
                          font-medium
                          text-red-600
                          transition
                          hover:bg-red-50
                        "
                      >
                        <span
                          className="
                            flex
                            h-8
                            w-8
                            items-center
                            justify-center
                            rounded-lg
                            bg-red-50
                            text-red-600
                          "
                        >
                          <FaTrash />
                        </span>

                        <span>Delete Account</span>
                      </button>

                      {/* LOGOUT */}

                      <button
                        type="button"
                        onClick={handleLogout}
                        className="
                          flex
                          w-full
                          items-center
                          gap-3
                          bg-gray-50
                          px-4
                          py-3
                          text-left
                          text-sm
                          font-bold
                          text-gray-700
                          transition
                          hover:bg-slate-100
                          hover:text-[#0f3d5e]
                        "
                      >
                        <span
                          className="
                            flex
                            h-8
                            w-8
                            items-center
                            justify-center
                            rounded-lg
                            bg-slate-200
                            text-slate-700
                          "
                        >
                          <FaSignOutAlt />
                        </span>

                        <span>Logout</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* =====================================================
            BOTTOM NAVIGATION COLOR STRIP
        ===================================================== */}

        <div
          className="
            h-1
            w-full
            bg-gradient-to-r
            from-[#0ea5e9]
            via-[#22d3ee]
            to-[#38bdf8]
          "
        />
      </header>

      {/* =====================================================
          NOTIFICATION MODAL
      ===================================================== */}

      {showNotifications && (
        <div
          className="
            fixed
            inset-0
            z-[9999]
            flex
            items-center
            justify-center
            bg-[#071525]/70
            p-3
            backdrop-blur-sm
            sm:p-5
          "
          onClick={() => setShowNotifications(false)}
        >
          <div
            className="
              flex
              max-h-[90vh]
              w-full
              max-w-lg
              flex-col
              overflow-hidden
              rounded-2xl
              border
              border-[#d8e3ee]
              bg-white
              shadow-[0_20px_60px_rgba(15,31,51,0.30)]
              sm:rounded-3xl
            "
            onClick={(event) => event.stopPropagation()}
          >
            {/* =================================================
                MODAL HEADER
            ================================================= */}

            <div
              className="
                flex
                shrink-0
                items-center
                justify-between
                border-b
                border-white/10
                bg-gradient-to-r
                from-[#0f1f33]
                via-[#1a3a5c]
                to-[#0ea5e9]
                px-4
                py-4
                text-white
                sm:px-5
              "
            >
              <div className="flex items-center gap-3">
                <div
                  className="
                    flex
                    h-9
                    w-9
                    items-center
                    justify-center
                    rounded-xl
                    border
                    border-white/10
                    bg-white/10
                    shadow-sm
                  "
                >
                  <FaBell />
                </div>

                <div>
                  <h2 className="text-base font-bold sm:text-lg">
                    Notifications
                  </h2>

                  <p className="text-[10px] text-cyan-100 sm:text-xs">
                    Your latest updates
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowNotifications(false)}
                aria-label="Close notifications"
                className="
                  flex
                  h-9
                  w-9
                  items-center
                  justify-center
                  rounded-xl
                  border
                  border-white/10
                  bg-white/10
                  transition
                  hover:bg-white/20
                "
              >
                <FaTimes />
              </button>
            </div>

            {/* =================================================
                NOTIFICATION CONTENT
            ================================================= */}

            <div
              className="
                min-h-0
                flex-1
                overflow-y-auto
                bg-[#f7fafc]
                p-4
                sm:p-5
              "
            >
              <Notification currentUser={user} />
            </div>

            {/* =================================================
                MODAL FOOTER
            ================================================= */}

            <div
              className="
                shrink-0
                border-t
                border-[#e2e8f0]
                bg-white
                px-4
                py-3
                sm:px-5
              "
            >
              <button
                type="button"
                onClick={() => setShowNotifications(false)}
                className="
                  w-full
                  rounded-xl
                  bg-gradient-to-r
                  from-[#1a3a5c]
                  to-[#0ea5e9]
                  px-4
                  py-2.5
                  text-sm
                  font-semibold
                  text-white
                  shadow-sm
                  transition
                  hover:opacity-95
                  hover:shadow-md
                "
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DashboardHeader;
